import { array, option, nonEmptyArray, record, string, predicate, ord } from 'fp-ts';
import { constNull, constant, pipe, flow, identity } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import {
    composeProps,
    createAsyncMetaSelectors,
    createPropsExtender,
    DateService,
    DateTime,
    DateTimeLocalizationInfo,
    DateTimeService,
    DateTimeUtcService,
    DateTimeUtils,
    DurationService,
    Month,
    MonthService,
    TimeUnitUtils,
} from '@mews/framework';
import { getProp, getStringArrayProp, getStringProp, primitiveTypeUniq, sumBy } from '@mews/fp';
import { ResolveRoute } from '@mews/routing';
import {
    ApplicationState,
    BlockAdjustmentData,
    Option as OptionType,
    ResourceCategoryAvailability,
    WithAvailabilityBlockId,
    WithId,
} from '@mews-commander/types';
import { RouteName } from '@mews-commander/enums';
import { TimeUnit } from '@mews/enums';
import {
    AvailabilityAdjustment,
    AvailabilityBlockState,
    Reservation,
    ReservationTimeState,
    ServiceOrderState,
} from '@mews-commander/data-access';
import { createHandler } from '@mews/std';
import { Option } from '@mews-commander/legacy-data-structures';
import {
    createTimeUnitRangeArray,
    dateTimeToUtc,
    getDateTimeUtc,
    getEarliestDayTime,
    utcToShortTimeString,
    getNowUtc,
    localizeEnumify,
    parseFormInt,
} from '@mews-commander/utils';
import { formValuesSelector } from '@mews-commander/legacy-form';
import { hasAvailabilityBlockAdjustments } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import {
    getAvailabilityBlockUtcDateRange,
    getAvailabilityBlock,
} from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import {
    getCurrentEnterpriseAppDateTimeNow,
    getTimeUnitRangeArrayFromUtc,
} from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import {
    getCurrentEnterpriseOperationalEditableHistoryWindow,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getServiceTimeUnit, getIsOverlappingNextTimeUnit } from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseReservationsByAvailabiltiyBlockId } from '@mews-commander/legacy-entities/src/selectors/reservations';
import {
    getCultureOrderedDayEnumValues,
    getCurrentDateTimeLocalizationInfo,
    getMonthNames,
} from '@mews-commander/core/legacy-globalization';
import { getReleaseInitialValues } from '../availabilityBlockEntityForm/selectors';
import { moduleSelector } from './module';
import { START_DATE, END_DATE, MAX_DAY_COUNT, START_HOUR, END_HOUR } from './constants';
import { EditDialog } from './enums';
import { ResourceCategoryField } from './types';
import { BulkAdjustAvailabilitiesFormValues } from './components/Dialogs/BulkAdjustAvailabilities/types';
import { getResourceCategoryBlockAdjustments } from './utils';

const adjustmentByStartUtc = ord.contramap((a: AvailabilityAdjustment) => a.startUtc)(string.Ord);

const getInitState = flow(moduleSelector, ({ init }) => init);

const getAdjustmentsState = flow(moduleSelector, ({ adjustments }) => adjustments);

const getAvailabilitiesState = flow(moduleSelector, ({ availabilities }) => availabilities);

const getInitMeta = flow(getInitState, ({ meta }) => meta);

const getAdjustmentsMeta = flow(getAdjustmentsState, ({ meta }) => meta);

const getAvailabilitiesMeta = flow(getAvailabilitiesState, ({ meta }) => meta);

export const [isInitFetching, hasInitFailed] = createAsyncMetaSelectors(getInitMeta);
export const [areAdjustmentsFetching, hasAdjustmentsFailed] = createAsyncMetaSelectors(getAdjustmentsMeta);
export const [areAvailabilitiesFetching, hasAvailabilitiesFailed] = createAsyncMetaSelectors(getAvailabilitiesMeta);

const getAvailabilities = flow(getInitState, ({ availabilities }) => availabilities);

export const getResourceCategoryAvailabilities = createSelector(
    getAvailabilities,
    nonEmptyArray.groupBy<ResourceCategoryAvailability>(({ resourceCategoryId }) => resourceCategoryId)
);

export const getBlockDateTimesAndTimeUnit = createSelector(
    (state, { serviceId }: { serviceId: string }) => getServiceTimeUnit(state, { id: serviceId }),
    getAvailabilityBlockUtcDateRange,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (timeUnit, { startUtc, endUtc }, timezone: string) => {
        if (!startUtc || !endUtc) return null;

        const startDateTime = DateTimeService.fromUtcString(startUtc, timezone);
        const endDateTime = DateTimeService.fromUtcString(endUtc, timezone);
        return { timeUnit, startDateTime, endDateTime };
    }
);

export const getDateTimeRangeArray = createSelector(
    getBlockDateTimesAndTimeUnit,
    (state, { serviceId }: { serviceId: string }) => getIsOverlappingNextTimeUnit(state, { id: serviceId }),
    (dateTimesAndTimeUnit, isOverlappingNextTimeUnit) => {
        if (!dateTimesAndTimeUnit) return [];

        // endUtc marks a last timeunit startUtc
        // for nightly (or other overlapping) services we add a checkout column represented by new timeunit
        const { timeUnit, startDateTime, endDateTime } = dateTimesAndTimeUnit;
        const modifiedEndDateTime =
            isOverlappingNextTimeUnit && timeUnit
                ? TimeUnitUtils.addTimeUnitToDateTime(endDateTime, timeUnit, 1)
                : endDateTime;

        return createTimeUnitRangeArray({
            timeUnit,
            startDateTime,
            endDateTime: modifiedEndDateTime,
            getRealHours: true,
        });
    }
);

const utcDateRangePropsExtender = createPropsExtender('utcDateRange', getAvailabilityBlockUtcDateRange);

const getTimeUnitsDateTimeRangeArray = composeProps(
    composeProps(getTimeUnitRangeArrayFromUtc, createPropsExtender('timeUnit', constant(TimeUnit.Day))),
    // @ts-expect-error $TSFixMeError: Type 'undefined' is not assignable to type 'string'.
    utcDateRangePropsExtender
);

type MapEntry = Pick<AvailabilityAdjustment, 'availabilityBlockId' | 'resourceCategoryId'>;

const getAdjustmentKey = <T extends MapEntry>({ availabilityBlockId, resourceCategoryId }: T) =>
    `${availabilityBlockId}-${resourceCategoryId}`;

/**
 *
 * @param sortedAdjustments - adjustments with same availability block and resource category, sorted by _startUtc_
 * @param startUtc - single point, for which we are looking adjustment (interval)
 * @returns adjustment, which in its interval contains _startUtc_
 */
const adjustmentIntervalBinarySearch = (sortedAdjustments: AvailabilityAdjustment[], startUtc: string) => {
    let startIndex = 0;
    let endIndex = sortedAdjustments.length - 1;

    while (startIndex <= endIndex) {
        const midIndex = Math.floor((startIndex + endIndex) / 2);
        const adjustment = sortedAdjustments[midIndex];

        // startUtc parameter must be withing closed interval
        // startUtc === endUtc in case when the adjustment is done on single timeunit level
        if (adjustment.startUtc <= startUtc && startUtc <= adjustment.endUtc) {
            return adjustment;
        } else if (adjustment.startUtc > startUtc) {
            endIndex = midIndex - 1;
        } else {
            startIndex = midIndex + 1;
        }
    }
};

export const cachedAvailabilityAdjustmentSelector = (adjustments: AvailabilityAdjustment[]) => {
    const sortedAdjustments = pipe(
        adjustments,
        nonEmptyArray.groupBy(getAdjustmentKey),
        record.map(array.sort(adjustmentByStartUtc))
    );

    return (entry: MapEntry & { startUtc: string }) => {
        const interval = sortedAdjustments[getAdjustmentKey(entry)];
        if (!interval) return null;
        return adjustmentIntervalBinarySearch(interval, entry.startUtc);
    };
};

export const getAdjustmentSelector = createSelector(getAvailabilities, (availabilities) => {
    const availabilityBlockAdjustments = pipe(
        availabilities,
        array.flatMap((availability) => availability.blockAdjustmentData),
        array.map((adjustmentData) => adjustmentData.availabilityBlockAdjustment)
    );

    return cachedAvailabilityAdjustmentSelector(availabilityBlockAdjustments);
});

export const getCurrentEnterpriseMinimalOperationEditableUtc = createSelector(
    getCurrentEnterpriseOperationalEditableHistoryWindow,
    getNowUtc,
    (operationEditableHistoryWindowDuration, nowUtc) => {
        const nowDateTimeUtc = DateTimeUtcService.parse(nowUtc);
        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'string'.
        const duration = DurationService.parse(operationEditableHistoryWindowDuration);

        return DateTimeUtcService.toISOString(
            DateTimeUtcService.addSeconds(nowDateTimeUtc, -1 * DurationService.asSeconds(duration))
        );
    }
);

const getFirstEditableDateTimeOption = createSelector(
    getTimeUnitsDateTimeRangeArray,
    getCurrentEnterpriseMinimalOperationEditableUtc,
    (timeUnitRange, editableHistoryUtc) =>
        array.findFirst((dateTime: DateTime) => dateTimeToUtc(dateTime) >= editableHistoryUtc)(timeUnitRange)
);

const getLastEditableDateTimeOption = createSelector(getTimeUnitsDateTimeRangeArray, array.last);

const dateTimeToLocalizedString = (dateTime: DateTime, localizationInfo: DateTimeLocalizationInfo) => {
    const date = DateTimeService.toDate(dateTime);
    return DateService.toString(date, localizationInfo);
};

const getSelectedDateTime = createSelector(
    (state: ApplicationState, { field }: { field: string }) =>
        formValueSelector(EditDialog.BulkAdjustAvailabilities)(state, field),
    getCurrentDateTimeLocalizationInfo,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (selectedDateString, localizationInfo, timezone: string) => {
        if (!selectedDateString || !DateService.isValidString(selectedDateString, localizationInfo)) return null;
        const selectedDate = DateService.fromString(selectedDateString, localizationInfo, timezone);
        return DateTimeService.fromDate(selectedDate);
    }
);

export const getBulkAdjustStartHourMinValue = createSelector(
    getFirstEditableDateTimeOption,
    getAvailabilityBlock,
    (state) => getSelectedDateTime(state, { field: START_DATE }),
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (firstEditableDateTimeOption, availabilityBlock, selectedDateTime, timezone: string) => {
        const startUtc = availabilityBlock?.startUtc;
        if (!selectedDateTime || !startUtc) return null;

        const blockStartDateTime = DateTimeService.fromUtcString(startUtc, timezone);
        const firstEditableDateTime = option.getOrElse(() => blockStartDateTime)(firstEditableDateTimeOption);

        const isSelectedDateGreaterThenBlockStart = DateTimeService.isGreater(selectedDateTime, blockStartDateTime);
        const isFirstEditableDateGreateThenBlockStart = DateTimeService.isGreater(
            firstEditableDateTime,
            blockStartDateTime
        );

        return utcToShortTimeString(
            isSelectedDateGreaterThenBlockStart || isFirstEditableDateGreateThenBlockStart
                ? DateTimeService.toUtc(selectedDateTime).value
                : startUtc,
            timezone
        );
    }
);

export const getBulkAdjustStartHourMaxValue = createSelector(
    getAvailabilityBlock,
    (state) => getSelectedDateTime(state, { field: START_DATE }),
    (state) => getSelectedDateTime(state, { field: END_DATE }),
    (state: ApplicationState) => formValueSelector(EditDialog.BulkAdjustAvailabilities)(state, END_HOUR),
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (availabilityBlock, selectedStartDateTime, selectedEndDateTime, selectedEndHour, timezone: string) => {
        const endUtc = availabilityBlock?.endUtc;
        if (!selectedStartDateTime || !selectedEndDateTime || !endUtc) return null;

        const isSelectedStartDateSameAsSelectedEndDate = DateTimeService.isEqual(
            selectedStartDateTime,
            selectedEndDateTime
        );

        if (isSelectedStartDateSameAsSelectedEndDate) {
            return selectedEndHour;
        }

        const blockEndDateTime = DateTimeService.fromUtcString(endUtc, timezone);
        const blockEndStartOfDay = DateTimeService.startOfDay(blockEndDateTime);
        const isSelectedStartDateSameAsBlockEnd = DateTimeService.isEqual(selectedStartDateTime, blockEndStartOfDay);
        const endOfDay = DateTimeService.endOfDay(selectedStartDateTime);

        return utcToShortTimeString(
            isSelectedStartDateSameAsBlockEnd ? endUtc : DateTimeService.toUtc(endOfDay).value,
            timezone
        );
    }
);

export const getBulkAdjustEndHourMinValue = createSelector(
    getAvailabilityBlock,
    (state) => getSelectedDateTime(state, { field: START_DATE }),
    (state) => getSelectedDateTime(state, { field: END_DATE }),
    (state: ApplicationState) => formValueSelector(EditDialog.BulkAdjustAvailabilities)(state, START_HOUR),
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (availabilityBlock, selectedStartDateTime, selectedEndDateTime, selectedStartHour, timezone: string) => {
        const startUtc = availabilityBlock?.startUtc;
        if (!selectedEndDateTime || !selectedStartDateTime || !startUtc) return null;

        const isSelectedEndDateSameAsSelectedStartDate = DateTimeService.isEqual(
            selectedStartDateTime,
            selectedEndDateTime
        );

        if (isSelectedEndDateSameAsSelectedStartDate) {
            return selectedStartHour;
        }

        const blockStartDateTime = DateTimeService.fromUtcString(startUtc, timezone);
        const blockStartOfDay = DateTimeService.startOfDay(blockStartDateTime);
        const isSelectedEndDateSameAsBlockStart = DateTimeService.isEqual(blockStartOfDay, selectedEndDateTime);

        return utcToShortTimeString(
            isSelectedEndDateSameAsBlockStart ? startUtc : DateTimeService.toUtc(selectedEndDateTime).value,
            timezone
        );
    }
);

export const getBulkAdjustEndHourMaxValue = createSelector(
    getAvailabilityBlock,
    (state) => getSelectedDateTime(state, { field: END_DATE }),
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (availabilityBlock, selectedDateTime, timezone: string) => {
        const startUtc = availabilityBlock?.startUtc;
        const endUtc = availabilityBlock?.endUtc;
        if (!selectedDateTime || !endUtc || !startUtc) return null;

        const blockEndDateTime = DateTimeService.fromUtcString(endUtc, timezone);
        const endOfDay = DateTimeService.endOfDay(selectedDateTime);
        const isSelectedDateLessThenBlockEnd = DateTimeService.isLess(endOfDay, blockEndDateTime);

        return utcToShortTimeString(
            isSelectedDateLessThenBlockEnd ? DateTimeService.toUtc(endOfDay).value : endUtc,
            timezone
        );
    }
);

export const getBulkUpdateAllowedDateTimeUnitData = createHandler<`${TimeUnit}`, $TSFixMe>(
    {
        [TimeUnit.Day]: {
            optionSelector: createSelector(
                getCultureOrderedDayEnumValues,
                array.map(
                    (enumValue): OptionType => Option({ value: enumValue.value, label: localizeEnumify(enumValue) })
                )
            ),
            dateTimeUnitIndexParser: (utc: string, timezone: string) =>
                DateTimeService.getDay(DateTimeService.fromUtcString(utc, timezone)),
            initialValues: createSelector(
                getCultureOrderedDayEnumValues,
                array.map(({ value }) => value)
            ),
        },
        [TimeUnit.Hour]: null,
        [TimeUnit.Month]: null,
    },
    null
);
const getBlockBulkUpdateAllowedDateTimeUnitData = (state: ApplicationState, { id }: WithId) => {
    const serviceId = getAvailabilityBlock(state, { id })?.serviceId;
    const timeUnit = serviceId ? getServiceTimeUnit(state, { id: serviceId }) : undefined;
    return getBulkUpdateAllowedDateTimeUnitData(timeUnit);
};
const getBlockInitialAllowedDateTimeUnitIndexes = (state: ApplicationState, { id }: WithId) =>
    getBlockBulkUpdateAllowedDateTimeUnitData(state, { id })?.initialValues(state);

const getBulkAdjustmentInitialAmountValue = (
    state: ApplicationState,
    { id, field }: WithId & { field: ResourceCategoryField }
) => {
    const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
    const localizationInfo = getCurrentDateTimeLocalizationInfo(state);
    const time = getEarliestDayTime(localizationInfo);

    const firstEditableDateTimeOption = getFirstEditableDateTimeOption(state, { id });
    const lastEditableDateTimeOption = getLastEditableDateTimeOption(state, { id });

    const formValues = formValuesSelector<BulkAdjustAvailabilitiesFormValues>(state, {
        form: EditDialog.BulkAdjustAvailabilities,
    });

    const startUtc =
        formValues?.startDate && DateService.isValidString(formValues.startDate, localizationInfo)
            ? getDateTimeUtc({
                  timezone,
                  localizationInfo,
                  date: formValues.startDate,
                  time: formValues?.startHour || time,
              })
            : option.match(constNull, dateTimeToUtc)(firstEditableDateTimeOption);

    const endUtc =
        formValues?.endDate && DateService.isValidString(formValues.endDate, localizationInfo)
            ? getDateTimeUtc({
                  timezone,
                  localizationInfo,
                  date: formValues.endDate,
                  time: formValues?.endHour || time,
              })
            : option.match(constNull, dateTimeToUtc)(lastEditableDateTimeOption);

    const allowedDateTimeUnitIndexes = formValues?.allowedDateTimeUnitIndexes;
    const dateTimeUnitIndexParser = getBlockBulkUpdateAllowedDateTimeUnitData(state, { id })?.dateTimeUnitIndexParser;

    return pipe(
        field.availabilities,
        array.filterMap((availability) =>
            (!startUtc || startUtc <= availability.dateTimeUtcForm) &&
            (!endUtc || endUtc >= availability.dateTimeUtcForm) &&
            (!allowedDateTimeUnitIndexes ||
                !dateTimeUnitIndexParser ||
                allowedDateTimeUnitIndexes.includes(dateTimeUnitIndexParser(availability.dateTimeUtcForm, timezone)))
                ? option.some(availability.adjustmentValue.value)
                : option.none
        ),
        primitiveTypeUniq,
        (uniqValues) => (uniqValues.length === 1 ? uniqValues[0] : null)
    );
};
export const getBulkAdjustAvailabilitiesInitialValues = createSelector(
    getBulkAdjustmentInitialAmountValue,
    getFirstEditableDateTimeOption,
    getLastEditableDateTimeOption,
    getCurrentDateTimeLocalizationInfo,
    getAvailabilityBlock,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getBlockInitialAllowedDateTimeUnitIndexes,
    (
        amountValue,
        startDateTimeOption,
        endDateTimeOption,
        localizationInfo,
        availabilityBlock,
        timezone: string,
        allowedDateTimeUnitIndexes
    ) => {
        const startUtc = availabilityBlock?.startUtc;
        const endUtc = availabilityBlock?.endUtc;
        return {
            amount: amountValue ?? '',
            startDate: option.match(
                () => null,
                (startDateTime: DateTime) => dateTimeToLocalizedString(startDateTime, localizationInfo)
            )(startDateTimeOption),
            startHour: startUtc ? utcToShortTimeString(startUtc, timezone) : '',
            endDate: option.match(
                () => null,
                (endDateTime: DateTime) => dateTimeToLocalizedString(endDateTime, localizationInfo)
            )(endDateTimeOption),
            endHour: endUtc ? utcToShortTimeString(endUtc, timezone) : '',
            allowedDateTimeUnitIndexes,
        };
    }
);

export const getBulkAdjustAvailabilitiesMaxDate = createSelector(getLastEditableDateTimeOption, (endDateTimeOption) =>
    option.match(
        () => null,
        (endDateTime: DateTime) => DateTimeUtils.dateTimeToStructuredDate(endDateTime)
    )(endDateTimeOption)
);

export const getBulkAdjustAvailabilitiesMinDate = createSelector(
    getFirstEditableDateTimeOption,
    (startDateTimeOption) =>
        option.match(
            () => null,
            (startDateTime: DateTime) => DateTimeUtils.dateTimeToStructuredDate(startDateTime)
        )(startDateTimeOption)
);

export const getResourceCategoryAdjustmentValue = createSelector(
    getResourceCategoryAvailabilities,
    getStringProp('dateTimeUtc'),
    getStringProp('resourceCategoryId'),
    getStringProp('availabilityBlockId'),
    (availabilities, dateTimeUtc, resourceCategoryId, availabilityBlockId) => {
        const adjustments = getResourceCategoryBlockAdjustments({
            categoryAvailabilities: availabilities[resourceCategoryId],
            dateTimeUtc,
            availabilityBlockId,
        });

        return sumBy(
            ({ remainingBlockAvailability, blockPickup }) => blockPickup + remainingBlockAvailability,
            adjustments
        );
    }
);

export const sumBlockPickUpValue = (
    availabilities: Record<string, ResourceCategoryAvailability[]>,
    dateTimeUtc: string,
    resourceCategoryId: string,
    availabilityBlockId: string
) => {
    const adjustments = getResourceCategoryBlockAdjustments({
        categoryAvailabilities: availabilities[resourceCategoryId],
        dateTimeUtc,
        availabilityBlockId,
    });

    return sumBy(({ blockPickup }) => blockPickup, adjustments);
};

export const getResourceCategoryPickedUpValue = createSelector(
    getResourceCategoryAvailabilities,
    getStringProp('dateTimeUtc'),
    getStringProp('resourceCategoryId'),
    getStringProp('availabilityBlockId'),
    sumBlockPickUpValue
);

export const isBlockAdjustmentReleased = createSelector(
    getResourceCategoryAvailabilities,
    getStringProp('dateTimeUtc'),
    getStringProp('resourceCategoryId'),
    getStringProp('availabilityBlockId'),
    (availabilities, dateTimeUtc, resourceCategoryId, availabilityBlockId) => {
        const adjustments = getResourceCategoryBlockAdjustments({
            categoryAvailabilities: availabilities[resourceCategoryId],
            dateTimeUtc,
            availabilityBlockId,
        });

        const sum = sumBy(
            (adj) => adj.blockAvailability - adj.blockPickup - adj.remainingBlockAvailability,
            adjustments
        );
        return sum > 0;
    }
);

export const getBlockAdjustmentData = createSelector(
    getAvailabilities,
    flow(
        array.filterMap(({ blockAdjustmentData }) =>
            array.isNonEmpty(blockAdjustmentData) ? option.some(blockAdjustmentData) : option.none
        ),
        array.flatten
    )
);

export const getBlockAdjustmentDataByAvailabilityBlockId = createSelector(
    getBlockAdjustmentData,
    getStringProp('availabilityBlockId'),
    (adjustmentData, availabilityBlockId) =>
        adjustmentData.filter((data) => data.availabilityBlockId === availabilityBlockId)
);

export const getSummaryBlockAdjustmentDataPickedUpValue = flow(
    getBlockAdjustmentDataByAvailabilityBlockId,
    sumBy(({ blockPickup }) => blockPickup)
);

export const getSummaryBlockAdjustmentDataBlockAvailability = flow(
    getBlockAdjustmentDataByAvailabilityBlockId,
    sumBy(({ blockAvailability }) => blockAvailability)
);

export const getSummaryBlockAdjustmentDataRemainingBlockAvailability = flow(
    getBlockAdjustmentDataByAvailabilityBlockId,
    sumBy(({ remainingBlockAvailability }) => remainingBlockAvailability)
);

export const getBlockAdjustmentReleaseNumber = createSelector(
    getSummaryBlockAdjustmentDataPickedUpValue,
    getSummaryBlockAdjustmentDataRemainingBlockAvailability,
    getSummaryBlockAdjustmentDataBlockAvailability,
    (summaryPickedUp, summaryRemainingBlockAvailability, summaryAvailability) =>
        summaryAvailability - (summaryPickedUp + summaryRemainingBlockAvailability)
);

export const getBlockAdjustmentAvailableNumber = createSelector(
    getSummaryBlockAdjustmentDataBlockAvailability,
    getSummaryBlockAdjustmentDataPickedUpValue,
    getBlockAdjustmentReleaseNumber,
    (summaryAvailability, summaryPickedUp, released) => summaryAvailability - summaryPickedUp - released
);

export const getOccupancy = createSelector(
    getSummaryBlockAdjustmentDataPickedUpValue,
    getSummaryBlockAdjustmentDataBlockAvailability,
    getBlockAdjustmentReleaseNumber,
    (summaryPickedUp, available, released) =>
        Number.isFinite(summaryPickedUp / (available - released)) ? summaryPickedUp / (available - released) : 0
);

export const getReservationCapacityByResourceCategory = createSelector(
    getAvailabilities,
    getStringProp('dateTimeUtc'),
    getStringProp('resourceCategoryId'),
    (availabilities, dateTimeUtc, resourceCategoryId) => {
        const findAvailability = availabilities.find(
            (availability) =>
                availability.timeUnitBaseStartUtc === dateTimeUtc &&
                availability.resourceCategoryId === resourceCategoryId
        );
        return findAvailability?.value || 0;
    }
);

const getTimeunitAvailabilities = createSelector(
    getAvailabilities,
    getStringProp('dateTimeUtc'),
    (availabilities, dateTimeUtc) =>
        availabilities.filter((availability) => availability.timeUnitBaseStartUtc === dateTimeUtc)
);

export const getAdjustmentDataByDate = createSelector(
    getTimeunitAvailabilities,
    getStringProp('availabilityBlockId'),
    (availabilities, availabilityBlockId) =>
        availabilities
            .flatMap(({ blockAdjustmentData }) => blockAdjustmentData)
            .filter((adjustmentData) => adjustmentData.availabilityBlockId === availabilityBlockId)
);

export const getTotalBlockAvailabilityCapacityByDate = flow(
    getAdjustmentDataByDate,
    sumBy(({ blockAvailability }) => blockAvailability)
);
export const getTotalBlockPickUpCapacityByDate = flow(
    getAdjustmentDataByDate,
    sumBy(({ blockPickup }) => blockPickup)
);

const isAdjustmentReleased = ({ blockAvailability, blockPickup, remainingBlockAvailability }: BlockAdjustmentData) =>
    blockAvailability - blockPickup - remainingBlockAvailability > 0;

export const areAllAdjustmentsReleasedByDate = createSelector(
    getAdjustmentDataByDate,
    array.every(isAdjustmentReleased)
);

export const getReleasedDateInitialValues = createSelector(
    getAvailabilityBlock,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrentDateTimeLocalizationInfo,
    (block, timezone: string, localizationInfo) => {
        if (block == null) return undefined;

        return getReleaseInitialValues(block, timezone, localizationInfo);
    }
);

const isFormEditMode = flow(getAdjustmentsState, ({ isEditMode }) => isEditMode);
export const wasReleaseOverridenInForm = flow(getAdjustmentsState, ({ wasReleaseOverriden }) => wasReleaseOverriden);

export const isAvailabilityEdited = createSelector(
    isFormEditMode,
    hasAvailabilityBlockAdjustments,
    (isEditMode, isEmptyAdjustments) => isEditMode || isEmptyAdjustments
);

export const getMaxStructureDateRelease = createSelector(
    getAvailabilityBlock,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (availabilityBlock, timezone: string) => {
        const startUtc = availabilityBlock?.startUtc;
        const endUtc = availabilityBlock?.endUtc;

        if (!startUtc || !endUtc) return null;
        const localizedDateTime = DateTimeService.fromUtcString(endUtc, timezone);
        return DateTimeUtils.dateTimeToStructuredDate(localizedDateTime);
    }
);

export const getNowStructuredDate = createSelector(
    getCurrentEnterpriseAppDateTimeNow,
    DateTimeUtils.dateTimeToStructuredDate
);

export const getSummaryAdjustmentValueFromForm = (
    state: ApplicationState,
    {
        availabilityBlockId,
        dateTimeUtc,
        arrayField,
    }: { availabilityBlockId: string; dateTimeUtc: string; arrayField: Array<ResourceCategoryField> }
) => {
    const adjustmentData = getAdjustmentDataByDate(state, { dateTimeUtc, availabilityBlockId });
    const releasedCategories = adjustmentData
        .filter(isAdjustmentReleased)
        .map((adjustment) => adjustment.availabilityBlockAdjustment.resourceCategoryId);

    const availabilities = arrayField
        .filter((resourceCategoryField) => !releasedCategories.includes(resourceCategoryField.resourceCategoryId))
        .flatMap((resourceCategoryField) => resourceCategoryField.availabilities);

    const filteredAvailabilities = availabilities.filter(
        ({ adjustmentValue, dateTimeUtcForm }) => dateTimeUtcForm === dateTimeUtc && adjustmentValue?.value
    );

    return sumBy(
        ({ adjustmentValue }) => (adjustmentValue.value ? parseFormInt(adjustmentValue.value) : 0),
        filteredAvailabilities
    );
};

export const getTotalBlocked = createSelector(
    getAdjustmentDataByDate,
    sumBy((adjustment) => (isAdjustmentReleased(adjustment) ? adjustment.blockPickup : adjustment.blockAvailability))
);

const getStartAndEndUtcFromBulkAdjustAvailabilitiesForm = (
    state: ApplicationState,
    { availabilityBlockId }: WithAvailabilityBlockId
) => {
    const availabilityBlock = getAvailabilityBlock(state, { id: availabilityBlockId });
    if (!availabilityBlock) throw new Error('Availability block not in state');

    const { startDate, endDate, startHour, endHour } = formValueSelector(EditDialog.BulkAdjustAvailabilities)(
        state,
        START_DATE,
        END_DATE,
        START_HOUR,
        END_HOUR
    );
    const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
    const localizationInfo = getCurrentDateTimeLocalizationInfo(state);
    const time = getEarliestDayTime(localizationInfo);
    const startUtc =
        startDate && DateService.isValidString(startDate, localizationInfo)
            ? getDateTimeUtc({ timezone, localizationInfo, date: startDate, time: startHour || time })
            : availabilityBlock?.startUtc;
    const endUtc =
        endDate && DateService.isValidString(endDate, localizationInfo)
            ? getDateTimeUtc({ timezone, localizationInfo, date: endDate, time: endHour || time })
            : availabilityBlock?.endUtc;
    return { startUtc, endUtc };
};

export const getTotalReservationCapacityByResourceCategoryInBlock = createSelector(
    getResourceCategoryAvailabilities,
    getStringProp('resourceCategoryId'),
    getStringProp('availabilityBlockId'),
    getStartAndEndUtcFromBulkAdjustAvailabilitiesForm,
    (availabilities, resourceCategoryId, availabilityBlockId, { startUtc, endUtc }) => {
        const filteredAvailabilities = availabilities[resourceCategoryId].filter(
            ({ timeUnitBaseStartUtc }) => timeUnitBaseStartUtc >= startUtc && timeUnitBaseStartUtc <= endUtc
        );
        return resourceCategoryId && filteredAvailabilities
            ? filteredAvailabilities.reduce((minValue, { blockAdjustmentData, value }) => {
                  const filteredBlockAdjustmentData = blockAdjustmentData.filter(
                      (data) => data.availabilityBlockId === availabilityBlockId
                  );
                  const sumValue =
                      value +
                      sumBy(
                          ({ remainingBlockAvailability, blockPickup }) => blockPickup + remainingBlockAvailability,
                          filteredBlockAdjustmentData
                      );
                  return sumValue < minValue ? sumValue : minValue;
              }, Number.MAX_SAFE_INTEGER)
            : 0;
    }
);

export const getResourceCategoryMaxPickedUpValue = createSelector(
    getResourceCategoryAvailabilities,
    getStartAndEndUtcFromBulkAdjustAvailabilitiesForm,
    getStringProp('resourceCategoryId'),
    getStringProp('availabilityBlockId'),
    (availabilities, { startUtc, endUtc }, resourceCategoryId, availabilityBlockId) => {
        const resourceCategoryAvailability = resourceCategoryId ? availabilities[resourceCategoryId] : undefined;
        const dateTimeRangeAvailabilities = resourceCategoryAvailability?.filter(
            ({ timeUnitBaseStartUtc }) => startUtc <= timeUnitBaseStartUtc && timeUnitBaseStartUtc <= endUtc
        );
        const dateTimeRangeBlockPickups = dateTimeRangeAvailabilities?.map((categoryAvailability) =>
            pipe(
                categoryAvailability.blockAdjustmentData,
                array.filter((adjustmentData) => adjustmentData.availabilityBlockId === availabilityBlockId),
                sumBy(({ blockPickup }) => blockPickup)
            )
        );

        return dateTimeRangeBlockPickups?.length ? Math.max(...dateTimeRangeBlockPickups) : 0;
    }
);

const getSumBlockAvailability = (adjustments: Array<BlockAdjustmentData>, resourceCategoryIds: Array<string>) => {
    const filterAdjustments = adjustments.filter(({ availabilityBlockAdjustment }) =>
        resourceCategoryIds.some(
            (resourceCategoryId) => resourceCategoryId === availabilityBlockAdjustment.resourceCategoryId
        )
    );

    return sumBy(({ blockAvailability }) => blockAvailability, filterAdjustments);
};

export const getAllReservationCapacityInBlockByDateFromForm = createSelector(
    getAvailabilities,
    getStringProp('dateTimeUtc'),
    getStringArrayProp('resourceCategories'),
    getAdjustmentDataByDate,
    composeProps(getAvailabilityBlock, createPropsExtender('id', getStringProp('availabilityBlockId'))),
    (availabilities, dateTimeUtc, resourceCategoryIdsFromForm, adjustments, availabilityBlock) => {
        const releasedCategories = adjustments
            .filter(isAdjustmentReleased)
            .map(({ availabilityBlockAdjustment: { resourceCategoryId } }) => resourceCategoryId);

        const usableAvailabilityEntities = availabilities.filter(
            ({ resourceCategoryId, timeUnitBaseStartUtc }) =>
                timeUnitBaseStartUtc === dateTimeUtc &&
                !releasedCategories.includes(resourceCategoryId) &&
                resourceCategoryIdsFromForm.includes(resourceCategoryId)
        );
        const sumBlockAvailability =
            availabilityBlock?.state === AvailabilityBlockState.Inquired
                ? 0
                : getSumBlockAvailability(
                      adjustments.filter(predicate.not(isAdjustmentReleased)),
                      resourceCategoryIdsFromForm
                  );

        return sumBy(({ value }) => value, usableAvailabilityEntities) + sumBlockAvailability;
    }
);

export const checkPickupInResourceCategory = createSelector(
    getResourceCategoryAvailabilities,
    getStringProp('availabilityBlockId'),
    getStringProp('resourceCategoryId'),
    (availabilities, availabilityBlockId, resourceCategoryId) =>
        availabilities[resourceCategoryId]?.some(
            ({ blockAdjustmentData }) =>
                blockAdjustmentData?.some(
                    (adjustment) => adjustment.availabilityBlockId === availabilityBlockId && adjustment.blockPickup
                )
        )
);

export const getCurrentMonth = flow(getAdjustmentsState, ({ month }) => month);

export const getCoveredMonths = createSelector(
    getAvailabilityBlock,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (availabilityBlock, timezone: string) => {
        const startUtc = availabilityBlock?.startUtc;
        const endUtc = availabilityBlock?.endUtc;

        if (!startUtc || !endUtc) {
            return [];
        }

        const startDateTime = DateTimeService.fromUtcString(startUtc, timezone);
        const endDateTime = DateTimeService.fromUtcString(
            TimeUnitUtils.addTimeUnitToUtc(endUtc, timezone, TimeUnit.Day, 1),
            timezone
        );

        const startMonth = DateTimeService.toMonth(startDateTime);
        const endMonth = DateTimeService.toMonth(endDateTime);

        const monthsDiff = MonthService.diffMonths(endMonth, startMonth);
        const monthsRange = nonEmptyArray.range(0, monthsDiff);

        return monthsRange.map((i) => MonthService.addMonths(startMonth, i));
    }
);

export const getMonthOptions = createSelector(getMonthNames, getCoveredMonths, (monthNames: Array<string>, months) =>
    months.map((month: Month, index: number) =>
        Option({
            value: index,
            label: `${monthNames[MonthService.getMonth(month)]} ${MonthService.getYear(month)}`,
        })
    )
);
export const getMonthInitialValue = createSelector(
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCoveredMonths,
    (timezone: string, months) => {
        const currentMonth = DateTimeService.toMonth(DateTimeService.fromUtcString(getNowUtc(), timezone));

        const currentMonthIndex = months.findIndex((m) => MonthService.isEqual(m, currentMonth));
        if (currentMonthIndex !== -1) return currentMonthIndex;

        const lastInPastIndex = months.findLastIndex((m) => MonthService.isLess(m, currentMonth));
        if (lastInPastIndex !== -1) return lastInPastIndex;

        return 0;
    }
);

export const isInMonthMode = createSelector(
    getAvailabilityBlock,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (state, { serviceId }: { serviceId: string }) => getServiceTimeUnit(state, { id: serviceId }),
    (availabilityBlock, timezone: string, timeUnit) => {
        const startUtc = availabilityBlock?.startUtc;
        const endUtc = availabilityBlock?.endUtc;

        if (!startUtc || !endUtc || timeUnit !== TimeUnit.Day) {
            return false;
        }

        const startDateTime = DateTimeService.fromUtcString(startUtc, timezone);
        const endDateTime = DateTimeService.fromUtcString(endUtc, timezone);

        return DateTimeService.diffDays(endDateTime, startDateTime) > MAX_DAY_COUNT;
    }
);

export const getMonth = createSelector(
    isInMonthMode,
    getCurrentMonth,
    getCoveredMonths,
    (inMonthMode, monthIndex, months) => {
        if (!inMonthMode) {
            return null;
        }

        return months[monthIndex];
    }
);

export const getAvailabilityBlockRateDetailUrl = createSelector(
    getAvailabilityBlock,
    getProp<ResolveRoute>()('resolveRoute'),
    (availabilityBlock, resolveRoute) => {
        const startUtc = availabilityBlock?.startUtc;
        const endUtc = availabilityBlock?.endUtc;
        const rateId = availabilityBlock?.rateId;
        return resolveRoute(RouteName.RatePricing, {
            rateId,
            query: {
                StartUtc: startUtc ?? '',
                EndUtc: endUtc ?? '',
            },
        });
    }
);

const getTimeStateGroupingKey = ({ reservationTimeState, serviceOrderState }: Reservation) => {
    if (serviceOrderState === ServiceOrderState.Canceled) return 'canceled';

    switch (reservationTimeState) {
        case ReservationTimeState.Future:
            return 'future';
        case ReservationTimeState.Past:
            return 'past';
        case ReservationTimeState.ToBeCheckedIn:
        case ReservationTimeState.CheckInMissed:
        case ReservationTimeState.CheckedIn:
        case ReservationTimeState.ToBeCheckedOut:
        case ReservationTimeState.CheckOutMissed:
            return 'inhouse';
        default:
            return 'other';
    }
};

export interface ReservationGroupData {
    from: string;
    to: string;
    count: number;
    groupId: string;
}
export const getAvailabilityBlockGroupData = createSelector(
    getCurrentEnterpriseReservationsByAvailabiltiyBlockId,
    flow(
        nonEmptyArray.groupBy(getTimeStateGroupingKey),
        identity<Record<ReturnType<typeof getTimeStateGroupingKey>, Reservation[]>>,
        ({ inhouse, past, future, canceled }) => ({
            inhouse: inhouse ?? [],
            past: past ?? [],
            future: future ?? [],
            canceled: canceled ?? [],
        }),
        record.map(
            flow(
                nonEmptyArray.groupBy((r) => r.groupId),
                record.mapWithIndex(
                    (groupId, groupReservations): ReservationGroupData => ({
                        from: pipe(
                            groupReservations,
                            nonEmptyArray.map((r) => r.startUtc),
                            nonEmptyArray.min(string.Ord)
                        ),
                        to: pipe(
                            groupReservations,
                            nonEmptyArray.map((r) => r.endUtc),
                            nonEmptyArray.max(string.Ord)
                        ),
                        count: groupReservations.length,
                        groupId,
                    })
                ),
                Object.values<ReservationGroupData>
            )
        )
    )
);
